/**
 * Section 04a — AutoTrade Lifecycle.
 *
 * The authoritative, code-accurate walk-through of the autonomous
 * Auto-Trade path: scheduled scan → RockBand V2 signal → gating ladder →
 * order creation (trade-first DRAFT, fill-confirm, promote OPEN) →
 * monitoring → trailing → exit. Every stage carries a worked example with
 * the EXACT quantity / ISL / target the backend computes, plus a scenario
 * matrix with outcomes.
 *
 * Verified against:
 *   backend/src/services/overviewScan.service.ts   (computeQty, computeAutoIslTarget call, fan-out)
 *   backend/src/services/orderQuantity.ts          (calculateOrderQuantity, splitQuantity)
 *   backend/src/services/alert.service.ts          (executeAutoTradeForAlert, createOrderFromAlert,
 *                                                   computeInvestmentEquivalentSl, withSymbolLock)
 *   backend/src/services/strategies/autoIslTarget.ts (computeAutoIslTarget)
 *   backend/src/services/tradingOrderGate.ts       (assertOrderingAllowed — isExit bypass)
 *   backend/src/services/trailingSlManager.service.ts (activation gate, HA trail, squareOff)
 */
import React from 'react';
import { Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, List, ListItem, ListItemText } from '@mui/material';
import AutoModeIcon from '@mui/icons-material/AutoMode';
import GuideCallout from '../GuideCallout';
import GuideExample from '../GuideExample';
import OpenInApp from '../OpenInApp';
import { GuideSection } from './types';

const H = ({ id, children }: { id: string; children: React.ReactNode }) => (
  <Typography id={`anchor-${id}`} variant="h5" sx={{ fontWeight: 700, mt: 4, mb: 1.25, scrollMarginTop: 80 }}>{children}</Typography>
);
const H3 = ({ id, children }: { id: string; children: React.ReactNode }) => (
  <Typography id={`anchor-${id}`} variant="h6" sx={{ fontWeight: 700, mt: 2.5, mb: 1, scrollMarginTop: 80, fontSize: '1.02rem' }}>{children}</Typography>
);
const P = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body1" sx={{ mb: 1.25, lineHeight: 1.65, fontSize: '0.95rem' }}>{children}</Typography>
);
const Code = ({ children }: { children: React.ReactNode }) => (
  <Box component="pre" sx={{ p: 1.25, bgcolor: 'action.hover', borderRadius: 1, fontSize: '0.78rem', overflow: 'auto', whiteSpace: 'pre-wrap', my: 1 }}>{children}</Box>
);

const Section_autotrade: GuideSection = {
  id: 'autotrade',
  label: 'AutoTrade Lifecycle',
  group: 'pages',
  icon: <AutoModeIcon fontSize="small" />,
  render: () => (
    <>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>AutoTrade Lifecycle</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        End-to-end, exactly as the engine runs it. When you turn <strong>Auto Trade ON</strong>, the
        in-house RockBand scanner places and manages real orders on the exchange with no manual click.
        This page traces every stage — from a scan firing to the position closing — with the precise
        quantity, stop-loss and target the backend computes, then a scenario matrix showing how each
        gate and edge case plays out.
      </Typography>

      <GuideCallout kind="danger" title="Real money, no confirmation">
        Auto-Trade places live broker orders automatically. There is no "are you sure?" step. The only
        switch that stops new orders reaching the exchange is <strong>Order Placement</strong> (the kill
        switch) on <OpenInApp to="/dashboard" label="Dashboard" /> → Trading Controls. Read the gating
        ladder (§4) before you flip Auto Trade ON.
      </GuideCallout>

      {/* ───────────────────────── TradeDesk ──────────────────────────────── */}
      <H id="tradedesk">TradeDesk — the single post-signal engine</H>
      <P>
        Everything after a signal is handled by <strong>one engine: TradeDesk</strong>. Strategies are
        analysts — each one (RockBand, Sniper, DailyBlast, a TradingView webhook, or any future strategy)
        only produces a <em>signal slip</em>:
      </P>
      <Code>{`StrategySignal  (the contract every strategy hands to TradeDesk)
  action       BUY | SHORT | SELL_FLAT | COVER_FLAT
  strength     HIGH | MEDIUM | LOW            (label only — never changes size)
  entryPrice   the signal-bar price
  isl          initial stop   (null → TradeDesk derives a fallback stop)
  target       target         (null → TradeDesk derives entry ± N×risk)
  ctfMinutes   signal timeframe (drives the trailing engine's candle TF)`}</Code>
      <P>
        TradeDesk owns the rest, identically for every strategy: the gating ladder, position sizing,
        crash-safe placement with confirmed fills, guaranteed SL/Target, monitoring, trailing,
        fill-confirmed exits, and cleanup. Each Trade is stamped with the strategy that opened it
        (attribution — visible on the Trades page and usable for per-strategy analytics).
      </P>

      {/* ───────────────────────── The path at a glance ───────────────────── */}
      <H id="overview">The path at a glance</H>
      <P>
        Autonomous signals come from <strong>scheduled scans</strong> (RockBand V2, Sniper) and
        <strong> TradingView webhooks</strong> — all of them flowing through TradeDesk. (The older RockBand
        and DailyBlast <em>polling</em> engines are retired — see the <strong>DailyBlast</strong> section.)
        The flow:
      </P>
      <Code>{`Scheduled scan (e.g. 1h + 4h)
   → RockBand V2 evaluates each tracked symbol on bar close
   → fresh BUY / SHORT / SELL_FLAT / COVER_FLAT signal  (per-candle dedup)
       → compute display Qty + Auto-ISL + Auto-Target
       → create Alert, route to Dashboard / Telegram
       → executeAutoTradeForAlert(...)   ← the gating ladder (§4)
            ENTRY: per-symbol lock → no-stacking → reserve → createOrderFromAlert
            EXIT : closePositionFromAlert (reduce-only, bypasses kill switch + cap)
   → tradeMonitor (30 s) watches SL / target
   → trailingSlManager ratchets the SL on the signal's timeframe
   → position closes (target / SL / trail / opposite signal / manual)`}</Code>
      <GuideCallout kind="info" title="One position per symbol — with auto-reverse on flips">
        The autonomous path takes <strong>at most one open position per symbol</strong>. While a trade is
        OPEN (or DRAFT), a further <em>same-side</em> entry signal for that symbol — from <em>any</em> scan
        timeframe — is skipped (<code>POSITION_OPEN</code>); there is no automatic scale-in / averaging-in on
        the auto path (scale-in splitting exists only on the <em>manual</em>-execute path; see §9). An
        <em> opposite</em>-side entry instead triggers <strong>auto-reverse (R147)</strong>: the open side is
        closed first (confirmed), then the new side opens — so a genuine strategy flip reverses rather than
        being dropped. Auto-reverse is governed by <strong>Settings → Auto-reverse</strong> (default ON); turn
        it off to make opposite signals skip with <code>POSITION_OPEN</code> like same-side ones.
      </GuideCallout>

      {/* ───────────────────────── Stage 0 — preconditions ────────────────── */}
      <H id="preconditions">Stage 0 — Preconditions &amp; configuration</H>
      <P>An auto-entry only happens when <strong>all</strong> of these are true:</P>
      <Table size="small" sx={{ my: 1 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Requirement</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Where</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>If missing</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow><TableCell><strong>Auto Trade = ON</strong> (global)</TableCell><TableCell>Dashboard → Trading Controls</TableCell><TableCell>No auto orders at all; signals still log + alert.</TableCell></TableRow>
          <TableRow><TableCell><strong>Order Placement = ON</strong> (kill switch)</TableCell><TableCell>Dashboard → Trading Controls</TableCell><TableCell>Entry blocked at broker layer. <em>Exits still fire.</em></TableCell></TableRow>
          <TableRow><TableCell><strong>Per-symbol Auto Long / Auto Short</strong></TableCell><TableCell>Instruments page (per row, per direction)</TableCell><TableCell>That direction skipped → <code>AUTO_LONG_OFF</code> / <code>AUTO_SHORT_OFF</code> chip.</TableCell></TableRow>
          <TableRow><TableCell><strong>A scheduled scan</strong> running RockBand on a watchlist</TableCell><TableCell>Strategies page → Scheduled Scans</TableCell><TableCell>No signals generated.</TableCell></TableRow>
          <TableRow><TableCell><strong>Investment amount</strong> on the instrument</TableCell><TableCell>Instruments page (Eq / Fut investment, per "Size as")</TableCell><TableCell>Entry skipped → <code>NO_SIZING_CONFIG</code> chip (no more silent 1-lot orders).</TableCell></TableRow>
          <TableRow><TableCell><strong>Live broker session</strong></TableCell><TableCell>Delta API keys / Zerodha token</TableCell><TableCell>Entry throws; alert flagged with the error; Telegram notice.</TableCell></TableRow>
          <TableRow><TableCell><strong>Free margin ≥ Portfolio Reserve</strong></TableCell><TableCell>Settings → Trading Preferences → Portfolio Reserve %</TableCell><TableCell>Entry skipped → <code>RESERVE</code> chip.</TableCell></TableRow>
        </TableBody>
      </Table>

      {/* ───────────────────────── Stage 1 — signal ───────────────────────── */}
      <H id="stage-signal">Stage 1 — A signal is born</H>
      <P>
        Each scheduled scan evaluates every tracked symbol on its interval. On a confirmed bar close,
        RockBand V2 emits one of four actions: <strong>BUY</strong> / <strong>SHORT</strong> (entries) or
        <strong> SELL_FLAT</strong> / <strong>COVER_FLAT</strong> (exits). A per-candle dedup guard means the
        same candle never fires twice. Exit signals are suppressed entirely when you hold no matching open
        position — they only fan out when there's something to close.
      </P>

      {/* ───────────────────────── Stage 2 — sizing ───────────────────────── */}
      <H id="stage-qty">Stage 2 — Quantity (the exact formula)</H>
      <P>
        The autonomous path sizes the <strong>full</strong> position and places it as a <strong>single
        order</strong> — it does <em>not</em> split. The integer lot count is:
      </P>
      <Code>{`Q  =  max( 1, floor( (Investment / fx × leverage) / (price × lotSize) ) )

  Investment  = your amount for the row's "Size as" (Instruments page)
                Futures → Fut Investment ; Equity → Eq Investment
                Auto (unset) = Futures on DELTA / NFO / BFO / MCX / CDS, else Equity
  fx          = 85  for DELTA / USD-quoted symbols, else 1   (₹ → $ conversion)
  leverage    = the instrument's leverage  (1 if unset; EQUITY & FUTURES Naked only)
  price       = live LTP at signal time
  lotSize     = contract / lot size of the instrument`}</Code>
      <GuideCallout kind="warning" title="What does NOT enter the quantity">
        Portfolio Reserve % is <strong>not</strong> baked into the quantity — it is a separate go/no-go
        <em> gate</em> (§4). Entry Splitting is <strong>not</strong> applied on the auto path. Entry-strength
        (HIGH/MED/LOW) is a label only and never changes size. For plain NSE/BSE equity the formula
        simplifies to <code>floor(Investment / price)</code> (no leverage, no lot multiplier).
      </GuideCallout>

      <GuideExample title="Stage 2 — LONG quantity on SOLUSD (DELTA perp)">
        <P>Instrument config: FUTURES · Naked · Fut Investment <strong>₹85,000</strong> · leverage <strong>10×</strong> · lotSize <strong>1</strong> (1 SOL/contract). Signal-bar LTP = <strong>$150.00</strong>. fx = 85 (DELTA).</P>
        <Code>{`Q = floor( (85000 / 85 × 10) / (150 × 1) )
  = floor( (1000 × 10) / 150 )
  = floor( 10000 / 150 )
  = floor( 66.67 )
  = 66 contracts`}</Code>
        <P>
          Notional = 66 × $150 = $9,900 (≈ ₹8.42 L). Margin at 10× ≈ $990 ≈ ₹84,150 — i.e. the position
          consumes ~your ₹85,000 investment as margin. ✔
        </P>
      </GuideExample>

      <GuideExample title="Stage 2 — SHORT quantity on AVAXUSD (DELTA perp)">
        <P>FUTURES · Naked · Fut Investment <strong>₹42,500</strong> · leverage <strong>5×</strong> · lotSize <strong>1</strong>. LTP = <strong>$20.00</strong>. fx = 85.</P>
        <Code>{`Q = floor( (42500 / 85 × 5) / (20 × 1) )
  = floor( (500 × 5) / 20 )
  = floor( 2500 / 20 )
  = 125 contracts`}</Code>
      </GuideExample>

      {/* ───────────────────────── Stage 3 — ISL & target ─────────────────── */}
      <H id="stage-isl">Stage 3 — Auto-ISL &amp; Auto-Target (the exact formula)</H>
      <P>
        Every fresh entry signal computes a stop-loss and a target from the two CLH candles immediately
        before the entry candle:
      </P>
      <Code>{`LONG  ISL = min( low  of prev-1 CLH, low  of prev-2 CLH )  −  breachBuffer
SHORT ISL = max( high of prev-1 CLH, high of prev-2 CLH )  +  breachBuffer

risk      = |entry − ISL|                       (signal rejected for auto-values if risk ≤ 0)
Target    = entry  ±  N × risk                  (N = Settings → Target → Target Risk Multiple, default 2)`}</Code>
      <P>
        The breach buffer (Settings → ISL) is <em>absolute</em>, <em>percent of entry</em>, or
        <em> ticks</em> (resolved against the real instrument tick — crypto ≠ 0.05). These values ride on
        the alert as <code>suggestedIsl</code> / <code>suggestedTarget</code> and seed the Trade row's
        <code> stopLoss</code> / <code>target</code> at creation.
      </P>

      <GuideExample title="Stage 3 — ISL / Target for the SOLUSD LONG (66 contracts)">
        <P>Entry $150.00. Prior two CLH candle lows: $144.80 and $144.00 → min = $144.00. Buffer = 0. N = 2.</P>
        <Code>{`ISL    = min(144.80, 144.00) − 0   = 144.00
risk   = 150.00 − 144.00            = 6.00 / unit
Target = 150.00 + 2 × 6.00          = 162.00

Risk if stopped  = 66 × 6.00  = $396   ≈ ₹33,660
Reward at target = 66 × 12.00 = $792   ≈ ₹67,320   (2R)`}</Code>
      </GuideExample>

      <H3 id="stage-isl-derived-target">Stage 3a — Signal carries an ISL but no Target</H3>
      <P>
        Some signal sources send only a stop (DailyBlast always; a TradingView webhook without a
        <code> TGT</code> field). The engine now <strong>derives the missing Target from the ISL</strong> —
        <code> Target = entry ± N × |entry − ISL|</code> — so every auto-entry has BOTH protective levels.
        Previously such trades had an SL but no Target: the target-hit exit could never fire and the
        "missing Target" reminder nagged hourly.
      </P>
      <GuideExample title="Stage 3a — DailyBlast SHORT on CRUDEOIL (MCX) with ISL only">
        <Code>{`Signal: SHORT @ ₹6,200 · ISL 6,290 (no target in the signal) · N = 2
risk   = 6,290 − 6,200 = 90
Target = 6,200 − 2 × 90 = 6,020   ← derived automatically

Trade row: SL 6,290 · Target 6,020 — monitor + trailing engage on both.`}</Code>
      </GuideExample>

      <H3 id="stage-isl-fallback">Stage 3b — Fallback when no ISL can be computed</H3>
      <P>
        If there are fewer than 3 candles, or the computed ISL lands on the wrong side of entry (risk ≤ 0),
        the strategy ISL is <code>null</code>. To guarantee <strong>no auto-entry is ever placed without a
        stop</strong>, the engine derives an <em>investment-equivalent</em> stop whose loss ≈ your configured
        investment:
      </P>
      <Code>{`perUnit = (Investment / fx) / (Q × lotSize)      (clamped 0 < perUnit ≤ 95% of entry)
ISL     = entry ∓ perUnit          (LONG −, SHORT +)
Target  = entry ± N × perUnit`}</Code>
      <GuideExample title="Stage 3b — Fallback ISL for the SOLUSD LONG">
        <Code>{`perUnit = (85000 / 85) / (66 × 1) = 1000 / 66 = $15.15
ISL     = 150.00 − 15.15 = $134.85
Target  = 150.00 + 2 × 15.15 = $180.30

Loss at ISL = 66 × 15.15 = $1,000 ≈ ₹85,000  ← ≈ your full investment`}</Code>
      </GuideExample>

      {/* ───────────────────────── Stage 4 — gating ladder ────────────────── */}
      <H id="stage-gates">Stage 4 — The gating ladder (entry only)</H>
      <P>
        Before any broker call, an entry signal passes through this exact sequence. The first gate that
        fails stamps an <strong>autoSkipReason</strong> on the alert (shown as a chip on the
        <OpenInApp to="/alerts" label="Alerts" /> page) and stops — no order is placed.
      </P>
      <Box component="ol" sx={{ pl: 3, fontSize: '0.93rem', lineHeight: 1.7 }}>
        <li><strong>Global gate</strong> — <code>Auto Trade ON</code> AND the signal source's own switch: for a scheduled scan that's its <em>Auto-Trade this scan</em> toggle (new — each scan can route signals without trading them); for DailyBlast the underlying's flag. Off → nothing happens (signals still log/route).</li>
        <li><strong>Direction gate</strong> — BUY needs the instrument's <em>Auto Long</em> = ON; SHORT needs <em>Auto Short</em> = ON. Off → <code>AUTO_LONG_OFF</code> / <code>AUTO_SHORT_OFF</code>. <strong>Now applies to TradingView webhooks too</strong> (they previously bypassed it — the webhook path runs through the same TradeDesk ladder since R132).</li>
        <li><strong>Per-symbol lock</strong> — a <code>{`{userId}:{instrumentId}`}</code> mutex serializes entries so two scans (e.g. 1h &amp; 4h) firing the same symbol at once cannot double-place.</li>
        <li><strong>No-stacking (with auto-reverse)</strong> — if an OPEN <em>or</em> DRAFT trade already exists for the symbol: a <em>same-side</em> signal → <code>POSITION_OPEN</code>, skip. An <em>opposite</em>-side signal (with Auto-reverse ON, the default) instead runs the reverse — close the open side first (confirmed), then proceed to open the new side; if the close can't be confirmed the new entry is held (<code>REVERSE_PENDING</code>). The close leg runs even when the per-direction Auto Long/Short gate would block a fresh entry, since closing is always allowed.</li>
        <li><strong>Portfolio Reserve</strong> — if placing this entry would drop free margin below your reserve % → <code>RESERVE</code>, skip.</li>
        <li><strong>Sizing config</strong> — the instrument must have an Investment amount for its segment/mode. Missing → <code>NO_SIZING_CONFIG</code>, skip (the old behaviour silently placed a <em>real 1-lot order</em> — that footgun is gone).</li>
        <li><strong>Place</strong> — only now does <code>createOrderFromAlert</code> run (Stage 5). The kill switch + daily-loss cap are enforced at the broker layer inside this step; blocked → <code>KILL_SWITCH</code> / <code>LOSS_CAP</code> skip (not an error).</li>
      </Box>

      <H3 id="skip-reasons">Skip-reason reference (the chips on the Alerts page)</H3>
      <P>
        Whenever the engine decides <em>not</em> to place an order, the alert carries an
        <code> autoSkipReason</code> so you always know <em>why</em> nothing happened. Skips are deliberate
        decisions — they show an info chip, not a red error, and Telegram notices for repeating skips are
        deduplicated to at most one per hour.
      </P>
      <Table size="small" sx={{ my: 1 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Chip</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Meaning</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Your action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow><TableCell><code>AUTO_LONG_OFF</code> / <code>AUTO_SHORT_OFF</code></TableCell><TableCell>Per-symbol direction toggle is off.</TableCell><TableCell>Flip it on the Instruments page, or execute manually.</TableCell></TableRow>
          <TableRow><TableCell><code>POSITION_OPEN</code></TableCell><TableCell>A <em>same-side</em> position (OPEN/DRAFT) already exists — no stacking. (An opposite-side signal auto-reverses instead, unless Auto-reverse is off.)</TableCell><TableCell>None — by design, one position per symbol.</TableCell></TableRow>
          <TableRow><TableCell><code>REVERSE_PENDING</code></TableCell><TableCell>Auto-reverse tried to close the opposite position but the close wasn't confirmed, so the new entry was held (avoids an accidental hedge).</TableCell><TableCell>None — retries; check the position closed cleanly on the broker.</TableCell></TableRow>
          <TableRow><TableCell><code>RESERVE</code></TableCell><TableCell>Free margin below your Portfolio Reserve %.</TableCell><TableCell>Free margin or lower the reserve.</TableCell></TableRow>
          <TableRow><TableCell><code>KILL_SWITCH</code></TableCell><TableCell>Order Placement is OFF — you paused trading.</TableCell><TableCell>Re-enable from Dashboard → Trading Controls when ready.</TableCell></TableRow>
          <TableRow><TableCell><code>LOSS_CAP</code></TableCell><TableCell>Daily-loss circuit breaker tripped for today.</TableCell><TableCell>Wait for tomorrow, or adjust the cap in Settings.</TableCell></TableRow>
          <TableRow><TableCell><code>GATE_UNAVAILABLE</code></TableCell><TableCell>The app could not read your kill-switch / loss-cap settings (database error), so the entry was refused to be safe.</TableCell><TableCell>Nothing; entries resume when the database answers. Exits are never affected.</TableCell></TableRow>
          <TableRow><TableCell><code>NO_SIZING_CONFIG</code></TableCell><TableCell>No Investment amount configured for the instrument.</TableCell><TableCell>Set Investment (+ leverage) on the Instruments page.</TableCell></TableRow>
          <TableRow><TableCell><code>NO_POSITION</code></TableCell><TableCell>An exit signal arrived but there was no matching-side position to close.</TableCell><TableCell>None — informational.</TableCell></TableRow>
          <TableRow><TableCell><code>QUALIFIER_WAIT</code> / <code>QUALIFIER_TIMEOUT</code> / <code>QUALIFIER_INVALID</code> / <code>QUALIFIER_CANCEL</code></TableCell><TableCell>Entry Qualifier states: parked for the 1m pullback / window expired / ISL crossed while waiting / cancelled by an exit signal.</TableCell><TableCell>See Dashboard → Pending AutoTrade Execution Watch.</TableCell></TableRow>
          <TableRow><TableCell><code>STRATEGY_MISMATCH</code></TableCell><TableCell>Strict Strategy Exits is ON and the position was opened by a different strategy.</TableCell><TableCell>None — the owning strategy's exits + SL/Target/trailing manage it.</TableCell></TableRow>
        </TableBody>
      </Table>

      <H3 id="strict-exits">Strict Strategy Exits (Settings → Trading Preferences)</H3>
      <P>
        With multiple strategies scanning the same symbols, an exit signal from one strategy can close a
        position another strategy opened (positions are one-per-symbol). By default that's allowed —
        more exits = more protection. Turn <strong>Strict Strategy Exits ON</strong> to scope signal exits
        to the strategy that opened the trade: a Sniper SELL_FLAT then no longer closes a RockBand long.
        SL, Target and Trailing exits are <strong>always active on every position</strong> regardless of
        this setting, and positions without strategy attribution (manual / webhook) remain closeable by
        any exit signal.
      </P>
      <GuideExample title="Example — strict exits with RockBand + Sniper on SOLUSD">
        RockBand opens SOL LONG (trade stamped strategy=RockBand). An hour later Sniper emits SELL_FLAT for
        SOL. Strict OFF (default): the position closes. Strict ON: the Sniper exit is skipped with a
        <code> STRATEGY_MISMATCH</code> chip; the position stays until RockBand's own exit — or its
        SL / Target / trailing stop — closes it.
      </GuideExample>
      <GuideCallout kind="info" title="Exits never touch this ladder">
        SELL_FLAT / COVER_FLAT (and SL / target / trailing / manual closes) skip the direction gate,
        no-stacking, reserve, kill switch <em>and</em> daily-loss cap. An open position can always be
        closed.
      </GuideCallout>

      <H3 id="entry-qualifier">Entry Qualifier — enter on the pullback (optional)</H3>
      <P>
        Most strategy signals are followed by a small pullback before the move continues. With the
        <strong> Entry Qualifier ON</strong> (toggled in the Dashboard's Pending AutoTrade Execution Watch
        header), TradeDesk parks each entry signal as a <strong>Pending AutoTrade Execution</strong> and fires
        only when the <strong>pullback timeframe</strong> confirms the pullback entry (evaluated on CLOSED bars
        of that timeframe). The pullback timeframe is selectable in the panel's ⚙ popover —
        <strong> 1m / 3m / 5m / 15m / 30m / 1H</strong>, default 1m:
      </P>
      <Code>{`BUY  fires when:  Trend score crosses UP through +20    (prev bar < 20, current >= 20)
              OR  candle low  <=  EMA#4               (pullback to the long-term average)
SHORT fires when: Trend score crosses DOWN through -20
              OR  candle high >=  EMA#4
…evaluated on the chosen pullback timeframe (default 1-minute).
EMA#4 = EMA over LinReg(close,14); period from your active chart template (default 200).`}</Code>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Validity window</strong> — default one signal-timeframe bar (1h signal → 60 min; configurable 5-240). If the pullback never comes the row EXPIRES with a Telegram notice — the inherent cost of pullback entries is occasionally missing a runaway move, and the alert makes that visible.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>ISL invalidation</strong> — price crossing the signal's ISL while waiting means the setup failed, not pulled back → INVALIDATED, no entry.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Exit-signal cancel</strong> — the strategy's exit for the symbol cancels its waiting entry (round trip over before entry).</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Re-gated at fire time</strong> — when it qualifies, the FULL ladder runs again (no-stacking, reserve, kill switch…). "Execute now" on the dashboard row skips the wait, never the ladder.</>} />
        </ListItem>
      </List>
      <GuideExample title="Worked example — RockBand BUY on SOLUSD with the Qualifier ON">
        <Code>{`14:00  RockBand 1h BUY @ $150.00 (ISL 144.00) → parked WAITING, valid till 15:00
14:07  1m score climbs 12 → 23  (crossed UP through +20)  → QUALIFIED
       TradeDesk ladder re-runs → entry fills @ $148.40 (the pullback price)
Result: same ISL 144.00, entry 1.60 better → risk/unit 4.40 instead of 6.00
        → the SAME trade is now ≈ +36% better in R terms.`}</Code>
      </GuideExample>
      <P>
        Watch and manage parked entries under <strong>Dashboard → Pending AutoTrade Execution Watch</strong> —
        live 1m score, EMA#4 distance, expiry countdown, Execute-now and Cancel per row.
      </P>

      {/* ───────────────────────── Stage 5 — order creation ───────────────── */}
      <H id="stage-create">Stage 5 — Order creation (trade-first, fill-confirmed)</H>
      <P>The placement routine is crash-safe and fill-accurate, in this order:</P>
      <Box component="ol" sx={{ pl: 3, fontSize: '0.93rem', lineHeight: 1.7 }}>
        <li>Resolve the live price (Delta LTP / Zerodha LTP) and compute Q (Stage 2).</li>
        <li>Resolve SL + Target (strategy values, or the Stage 3b fallback) — so the row <em>always</em> has both.</li>
        <li><strong>Create the Trade row FIRST as <code>DRAFT</code></strong>, carrying the SL + Target, the origin timeframe, and <code>autoTrailEligibleAt = open + 2 × interval</code>. (Trade-first means a crash after the broker fill can never orphan a live position with no app row + no stop.)</li>
        <li>Create the linked Order row (PENDING) and place it on the broker — <strong>Delta = MARKET</strong>, <strong>Zerodha = LIMIT</strong>.</li>
        <li><strong>Confirm the fill</strong> (Delta: poll up to 6 s for the real average fill price &amp; quantity).</li>
        <li>Mark the order FILLED at the confirmed average and <strong>promote DRAFT → OPEN</strong> at that price. (Zerodha LIMIT stays DRAFT until position-sync sees the fill.)</li>
        <li>The kill switch + daily-loss cap are enforced <em>here</em>, at the broker layer, for entries only. If a broker placement fails, the order is REJECTED and the just-created DRAFT trade is <strong>deleted</strong> — no phantom OPEN.</li>
      </Box>

      <GuideExample title="Stage 5 — the SOLUSD LONG becomes a live position">
        <Code>{`DRAFT trade  : SOL LONG · qty 66 · SL 144.00 · target 162.00 · interval 60m
Broker order : Delta MARKET buy 66  → filled avg $150.10
Confirm fill : avgPrice 150.10, filledQty 66
Promote      : trade → OPEN · averageEntryPrice 150.10
Result       : one OPEN long, 66 contracts, protected by SL 144.00 + target 162.00`}</Code>
      </GuideExample>

      {/* ───────────────────────── Stage 6 — monitoring ───────────────────── */}
      <H id="stage-monitor">Stage 6 — Monitoring (SL &amp; target)</H>
      <P>
        Trade Monitor wakes every <strong>~30 s</strong>. For each OPEN trade it refreshes the live price and
        checks the operative SL and target. On a breach it fires a <strong>full-size, reduce-only</strong>{' '}
        close — the close quantity is read from the <em>actual live position size</em> on the exchange (not a
        possibly-stale stored quantity), so no residual sliver is left behind. A Delta-price watchdog
        notifies you on Telegram if Delta data goes dark for &gt; 5 min and again on recovery.
      </P>

      <H3 id="fastlane">FastLane — per-second protection when it matters</H3>
      <P>
        The 30-second monitor cadence is fine for quiet positions, but a spike can give back several R
        between ticks. <strong>FastLane</strong> (Settings → TSL, default ON) watches <em>hot</em> trades —
        spike mode active, or a breach-confirmation hold window running — every <strong>~1 second</strong>{' '}
        (Zerodha floored to 2s, one batched quote call). Each fast tick is deliberately cheap: refresh the
        favourable-excursion peak, ratchet the spike profit-lock from the new peak, and run the breach /
        target checks against the already-computed levels. The full trail recompute stays on the 30s cycle,
        a per-trade mutex stops the two loops ever double-firing, and API trouble backs FastLane off while
        the 30s monitor keeps protecting.
      </P>
      <GuideExample title="Why it matters — spike reversal on SOLUSD">
        <Code>{`Spike mode ON, profit-lock SL 161.20, price 168.50 and falling fast.
30s cadence: next check up to 30s away — price can be 163 by then; the
             60s spike hold starts LATE and confirms LATE.
FastLane:    breach detected <=1s after 161.20 trades; the 60s spike hold
             window starts immediately and the square-off fires on time.`}</Code>
      </GuideExample>

      {/* ───────────────────────── Stage 7 — trailing ─────────────────────── */}
      <H id="stage-trail">Stage 7 — Trailing stop (on the signal's timeframe)</H>
      <P>
        The trailing-SL engine ratchets the stop in your favour. Two gates must clear before it begins
        tightening:
      </P>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Time gate</strong> — not before <code>open + 2 × originIntervalMinutes</code>. A 1h-signal trade is eligible 2h after entry; a 4h-signal trade after 8h.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>R-multiple gate</strong> — Maximum Favourable Excursion (MFE) ≥ <em>Trail SL Activation × R</em> (Settings → TSL, default <strong>2R</strong>). Set to 0 = trail from the first eligible bar.</>} />
        </ListItem>
      </List>
      <P>
        Once active, the trail follows Heikin-Ashi candles <strong>on the trade's origin timeframe</strong>{' '}
        (the timeframe of the scan that opened it) — LONG trails to <code>min(HA-low of last 3 HA candles)</code>,
        SHORT to <code>max(HA-high of last 3)</code>. The ratchet is <strong>monotonic</strong>: the stop only
        moves toward profit, never back. <em>Spike mode</em> tightens the breach-hold and stages profit-locks at
        2R / 3R / 4R on exceptional moves. A confirmed trail breach closes the <strong>entire</strong> live
        position (reduce-only).
      </P>

      <GuideExample title="Stage 7 — trailing ratchet on the SOLUSD LONG (1h origin)">
        <Code>{`Entry 150.10, ISL 144.00 (risk 6.00/unit), activation 2R, origin 60m.

t+2h  price 162.00 → MFE = (162.00−150.10)/6.00 = 1.98R  → still WAITING (<2R)
t+3h  price 164.50 → MFE ≈ 2.4R → ACTIVATED; HA-trail lifts SL to 156.20
t+4h  price 171.00 → SL ratchets up to 162.80   (now locked above entry)
t+5h  price falls, HA-low breached at 162.80 → full-size reduce-only close

Outcome: exit ≈ 162.80 → +12.70/unit × 66 = +$838 ≈ +₹71,200  (≈ +2.1R)`}</Code>
      </GuideExample>

      {/* ───────────────────────── Stage 8 — exits ────────────────────────── */}
      <H id="stage-exit">Stage 8 — How a position closes</H>
      <Table size="small" sx={{ my: 1 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Exit path</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Trigger</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Bypasses gates?</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow><TableCell>Target hit</TableCell><TableCell>Trade Monitor sees price ≥/≤ target</TableCell><TableCell>Yes (reduce-only)</TableCell></TableRow>
          <TableRow><TableCell>Stop-loss breach</TableCell><TableCell>Trade Monitor sees price past operative SL</TableCell><TableCell>Yes (reduce-only)</TableCell></TableRow>
          <TableRow><TableCell>Trailing breach</TableCell><TableCell>HA-trail level broken (Stage 7)</TableCell><TableCell>Yes (reduce-only)</TableCell></TableRow>
          <TableRow><TableCell>Opposite signal</TableCell><TableCell>SELL_FLAT / COVER_FLAT from a scan</TableCell><TableCell>Yes</TableCell></TableRow>
          <TableRow><TableCell>Manual close</TableCell><TableCell>You click Close / Square-Off</TableCell><TableCell>Yes</TableCell></TableRow>
        </TableBody>
      </Table>
      <P>
        Every exit is reduce-only and full-size, and bypasses the kill switch + daily-loss cap, so a position
        is always closeable even when entries are frozen.
      </P>

      <H3 id="stage-exit-confirm">Signal exits are fill-confirmed (no ghost closes)</H3>
      <P>
        A SELL_FLAT / COVER_FLAT exit follows the same crash-safe discipline as entries:
      </P>
      <Box component="ol" sx={{ pl: 3, fontSize: '0.93rem', lineHeight: 1.7 }}>
        <li>The exit runs inside the same per-symbol lock as entries and re-checks the trade inside it — concurrent exits (1h + 4h scans, duplicate webhooks) can never double-close.</li>
        <li><strong>NSE:</strong> before ordering, the close quantity is clamped to the <em>live</em> Zerodha position (positions + CNC holdings). If you partially closed on the broker minutes ago, the app sells only what actually exists — it can no longer oversell into a naked short. If the broker shows flat, the trade is reconciled CLOSED with <strong>no order placed</strong>.</li>
        <li>The close is placed (Delta reduce-only MARKET / Zerodha LIMIT ±0.5%) and the <strong>fill is confirmed</strong> (up to 8 s).</li>
        <li><strong>Confirmed</strong> → the trade closes at the actual fill price with full close metadata (<code>closePrice</code>, <code>closeQuantity</code>, <code>closeMethod = signal_exit</code>), and any <strong>resting Pending Entries for the symbol are cancelled</strong> so they can't re-open the position.</li>
        <li><strong>Not confirmed</strong> (e.g. the NSE LIMIT missed in a fast move) → the trade is <strong>kept OPEN</strong> and flagged, you get a Telegram notice, and the order stays PENDING for the monitor's next-tick reconciliation. The app never claims a close the broker didn't deliver.</li>
      </Box>

      <H3 id="stage-exit-draft">Exit signal while the entry hasn't filled yet</H3>
      <P>
        If the exit signal arrives while the entry order is still resting at the broker (an NSE LIMIT entry
        that never filled — trade still DRAFT), the engine <strong>cancels the resting entry order</strong>{' '}
        instead: the strategy's round-trip is over, so there is nothing to hold. You get a Telegram
        "Entry cancelled" notice. Previously the exit was simply ignored and the entry could fill
        later — opening a position whose exit signal had already passed.
      </P>
      <GuideExample title="Worked example — exit arrives before the NSE entry fills">
        <Code>{`10:15  RockBand BUY RELIANCE → Zerodha LIMIT buy 40 @ ₹2,950 (DRAFT, unfilled)
11:15  next scan emits SELL_FLAT (signal reversed before the dip filled)
   →  resting LIMIT buy CANCELLED at Zerodha, DRAFT row closed ('cancelled')
   →  Telegram: "Entry cancelled — SELL_FLAT exit signal arrived before fill"
Result: no position, no orphaned order. Nothing to manage.`}</Code>
      </GuideExample>

      {/* ───────────────────────── Scenario matrix ────────────────────────── */}
      <H id="scenarios">Scenario matrix — what actually happens</H>

      <GuideExample title="S1 — Happy-path LONG to target">
        SOLUSD: Q 66, entry 150.10, ISL 144.00, target 162.00. Price runs to 162.00 before the trail
        activates. Trade Monitor closes 66 contracts reduce-only at ≈162.00. <strong>Outcome: +₹67,320 (≈ +2R)</strong>.
      </GuideExample>

      <GuideExample title="S2 — SHORT that trails out in profit">
        AVAXUSD: Q 125, entry 20.00, ISL 21.20 (risk 1.20). Price drops; past 2R MFE the HA-trail ratchets the
        stop down to 18.10. A pullback breaks 18.10 → full-size reduce-only buy-to-cover.
        <strong> Outcome: +1.90/unit × 125 = +$237.50 ≈ +₹20,190 (≈ +1.6R)</strong>.
      </GuideExample>

      <GuideExample title="S3 — No strategy ISL → investment-equivalent fallback">
        SOLUSD LONG fires with only 2 prior candles available. Strategy ISL = null → fallback kicks in: ISL
        134.85, target 180.30 (Stage 3b). The trade opens fully protected. <strong>Outcome: an SL is always
        present; loss-at-stop ≈ your ₹85,000 investment.</strong>
      </GuideExample>

      <GuideExample title="S4 — Same symbol, two timeframes (no stacking)">
        The 1h scan opens SOL LONG. Twenty minutes later the 4h scan emits SOL BUY too. Gate 4 sees an OPEN
        trade → <code>POSITION_OPEN</code>, orange chip on Alerts, <strong>no second order</strong>. The 1h
        position rides on unchanged.
      </GuideExample>

      <GuideExample title="S5 — Concurrent fire, same instant (mutex)">
        1h and 4h scans both produce SOL BUY in the same cycle. The per-symbol lock serializes them: the first
        opens the trade; the second, now seeing it OPEN, hits <code>POSITION_OPEN</code>.
        <strong> Outcome: exactly one order, never two.</strong>
      </GuideExample>

      <GuideExample title="S6 — Portfolio Reserve guard">
        Reserve = 40%, free margin already low. A fresh BUY would breach the reserve → <code>RESERVE</code>{' '}
        chip, <strong>no order</strong>. Existing positions are untouched. Free up margin (or lower the
        reserve) and the next signal can enter.
      </GuideExample>

      <GuideExample title="S7 — Per-symbol direction OFF">
        Instrument has Auto Short = OFF. A SHORT signal arrives → <code>AUTO_SHORT_OFF</code> chip, no order.
        You can flip Auto Short ON for future signals, or execute this one manually from Alerts. Auto Long is
        independent and still active.
      </GuideExample>

      <GuideExample title="S8 — Kill switch ON mid-trade (Order Placement OFF)">
        You flip Order Placement OFF while SOL LONG is open. <strong>New entries are blocked</strong> at the
        broker layer. But the open trade's SL / target / trailing <strong>still fire</strong> — an SL breach
        closes the full position reduce-only. Re-enable Order Placement to allow fresh entries again.
        While the switch is OFF, each blocked signal shows a <code>KILL_SWITCH</code> chip on Alerts (info,
        not a red error) and Telegram reminds you <strong>at most once per hour</strong> — not once per
        signal as before.
      </GuideExample>

      <GuideExample title="S12 — Exit LIMIT misses on NSE (no ghost close)">
        TATASTEEL LONG 100 shares; SELL_FLAT fires at LTP ₹145.00 → Zerodha LIMIT sell @ ₹144.30 (−0.5%).
        Price gaps to ₹143.50 before the LIMIT fills. Fill is NOT confirmed within 8 s →
        <strong> the trade stays OPEN</strong> and flagged, Telegram warns you, and the unfilled order is
        reconciled on the next monitor tick. The app never marks a close the broker didn't deliver — your
        SL keeps protecting the still-live position meanwhile.
      </GuideExample>

      <GuideExample title="S13 — You partially closed at the broker, then the exit signal fires">
        SOL LONG 66 in the app; you sold 30 directly on Delta's own app five minutes ago (sync hasn't
        caught up). A SELL_FLAT exit then fires. On Delta the close is <strong>reduce-only</strong>, so it
        can only flatten what exists. On NSE the engine first reads the <strong>live broker position</strong>{' '}
        and clamps the close to it (sell 36, not 66) — no oversell, no accidental short.
        If the broker shows fully flat, the trade is just reconciled CLOSED with no order at all.
      </GuideExample>

      <GuideExample title="S14 — Webhook for an unconfigured symbol">
        A TradingView webhook fires <code>BUY DOGEUSD</code>, a symbol you auto-tracked but never configured.
        Old behaviour: a real 1-contract order went out silently. Now: <code>NO_SIZING_CONFIG</code> chip,
        no order, and one Telegram telling you to set the Investment amount on the Instruments page if you
        actually want it traded. Set it once — the next signal trades at the computed size.
      </GuideExample>

      <GuideExample title="S15 — Signal exit cleans up everything it should">
        SOL LONG closes on SELL_FLAT at a confirmed $158.20 fill. The trade row gets
        <code> closePrice 158.20 · closeMethod signal_exit</code> (visible in Analytics), and the two resting
        scale-in legs you had queued for SOL from the chart are <strong>auto-cancelled</strong> — they no
        longer fire later and silently re-open the position you just exited.
      </GuideExample>

      <GuideExample title="S9 — Daily-loss cap tripped">
        Cap = ₹10,000; today's realised + open drawdown ≥ ₹10,000. Every <strong>new entry</strong> is
        rejected for the rest of the day. <strong>Exits are unaffected</strong> — your open positions can
        still hit SL / target / trail and close. Adjust or disable the cap from Settings → Trading Preferences.
      </GuideExample>

      <GuideExample title="S10 — Broker rejects the entry">
        The broker rejects the SOL order (e.g. margin/precision). The order is marked REJECTED, the
        just-created DRAFT trade is <strong>deleted</strong> (no phantom OPEN row), and you get a Telegram
        notice. The signal stays on Alerts with the error so you can act manually.
      </GuideExample>

      <GuideExample title="S11 — Broker session expires">
        Zerodha token (or Delta keys) goes invalid mid-day. The entry throws; the alert is flagged and a
        Telegram notice is sent. SL/Target monitoring + trailing pause for affected positions until you
        re-login, then resume from the last cached state.
      </GuideExample>

      {/* ───────────────────────── User control ───────────────────────────── */}
      <H id="user-control">What you can change while a trade runs</H>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Edit SL / Target any time</strong> — inline on Trade Management or by dragging on Charts (Add ISL / Add Tgt). Works the same whether Auto Trade is ON or OFF; the change is pushed to the broker bracket. The auto path never silently overwrites an SL/Target you set.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Snooze / disable the per-trade trail</strong> — the Auto-Trail widget on the row freezes trailing for a chosen window while keeping the current SL operative.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Flip the SL Trigger switch</strong> — turn the automatic SL-close off for a single trade (SL becomes alert-only). Use with care.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Close manually</strong> any time, or globally via Square-Off All / Long / Short on the Dashboard.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Toggle the global switches</strong> — Auto Trade and Order Placement on the Dashboard affect only future signals, never open positions.</>} />
        </ListItem>
      </List>
      <P><strong>Not directly editable:</strong> the computed quantity (driven by Investment × leverage ÷ price ÷ lotSize) and the scan cadence. You can't flip a trade's side in place — but you don't have to: with Auto-reverse ON an opposite-side signal closes the position and opens the other side for you, or you can close it manually and open the opposite side yourself.</P>

      {/* ───────────────────────── Manual vs Auto ─────────────────────────── */}
      <H id="manual-vs-auto">Manual execute vs Auto-Trade — the one real difference</H>
      <P>
        When you execute a signal <em>manually</em> (Alerts → Execute, Charts BUY/SHORT, Dashboard parked
        alerts), the platform sizes the <strong>same total Q</strong> but can split it into legs per your
        <strong> Entry Splitting</strong> setting using a doubling rule
        (<code>N=3 → ¼, ¼, ½</code>), and the <strong>Portfolio Reserve is applied as part of manual-execute
        sizing</strong>. The autonomous Auto-Trade path does neither: it places the full Q as one order and
        treats Reserve purely as a go/no-go gate. ISL/Target, trailing, monitoring and exits are identical
        across both paths. See the <strong>Trades</strong> section (Trade lifecycle) for the manual worked
        example.
      </P>

      <GuideCallout kind="tip" title="Recommended first run">
        Start with Auto Long/Short ON for just one or two symbols, a conservative Portfolio Reserve, and the
        daily-loss cap enabled. Watch a full cycle (entry → trail → exit) on the Trades page before widening
        the symbol set. Keep Order Placement reachable on the Dashboard as your instant cut-off.
      </GuideCallout>
    </>
  ),
  searchIndex: [
    { heading: 'TradeDesk — the single engine',  anchor: 'tradedesk',         body: 'tradedesk single post signal engine strategy signal contract attribution strategies analysts dealing desk' },
    { heading: 'Strict Strategy Exits',          anchor: 'strict-exits',      body: 'strict strategy exits strategy mismatch cross strategy exit attribution settings trading preferences' },
    { heading: 'Entry Qualifier — 1m pullback',  anchor: 'entry-qualifier',   body: 'entry qualifier pullback 1 minute trend score 20 ema4 ema 200 ttl invalidated expired execute now pending autotrade execution watch' },
    { heading: 'FastLane — per-second protection', anchor: 'fastlane',        body: 'fastlane fast lane per second polling spike mode breach hold window profit lock ratchet 1s 2s zerodha' },
    { heading: 'AutoTrade path at a glance',     anchor: 'overview',          body: 'autotrade auto trade lifecycle scheduled scan rockband v2 sniper webhook one position per symbol no stacking' },
    { heading: 'Preconditions & configuration',  anchor: 'preconditions',     body: 'auto trade on order placement kill switch auto long auto short investment portfolio reserve broker session' },
    { heading: 'Stage 1 — signal',               anchor: 'stage-signal',      body: 'signal buy short sell flat cover flat bar close per candle dedup exit suppressed' },
    { heading: 'Stage 2 — quantity formula',     anchor: 'stage-qty',         body: 'quantity formula lots leverage lotsize fx 85 investment floor single order no split' },
    { heading: 'Stage 3 — auto ISL and target',  anchor: 'stage-isl',         body: 'auto isl auto target prev 2 clh candles breach buffer risk n multiple suggestedIsl suggestedTarget' },
    { heading: 'Stage 3a — Target derived from ISL', anchor: 'stage-isl-derived-target', body: 'target derived from isl dailyblast webhook no tgt entry plus minus n risk' },
    { heading: 'Stage 3b — ISL fallback',        anchor: 'stage-isl-fallback',body: 'fallback investment equivalent stop loss perunit clamp no entry without stop' },
    { heading: 'Stage 4 — gating ladder',        anchor: 'stage-gates',       body: 'gating ladder auto long off auto short off mutex lock position open reserve skip autoSkipReason no sizing config kill switch loss cap' },
    { heading: 'Skip-reason reference',          anchor: 'skip-reasons',      body: 'skip reason chips auto skip kill switch loss cap no sizing config no position reserve position open hourly telegram' },
    { heading: 'Stage 5 — order creation',       anchor: 'stage-create',      body: 'trade first draft fill confirm promote open delta market zerodha limit broker reject delete phantom' },
    { heading: 'Stage 6 — monitoring',           anchor: 'stage-monitor',     body: 'trade monitor 30 seconds sl target reduce only full size live position watchdog' },
    { heading: 'Stage 7 — trailing stop',        anchor: 'stage-trail',       body: 'trailing stop origin timeframe heikin ashi ratchet activation r mfe spike mode profit lock' },
    { heading: 'Stage 8 — exits',                anchor: 'stage-exit',        body: 'exit target stop loss trailing opposite signal manual close reduce only bypass kill switch cap' },
    { heading: 'Signal exits are fill-confirmed', anchor: 'stage-exit-confirm', body: 'fill confirmed ghost close signal exit close price close method live qty clamp naked short pending entries cancelled' },
    { heading: 'Exit while entry unfilled',      anchor: 'stage-exit-draft',  body: 'exit signal draft unfilled entry cancelled resting limit order' },
    { heading: 'Scenario matrix',                anchor: 'scenarios',         body: 'scenarios happy path long short trail fallback position open mutex reserve direction off kill switch daily loss cap broker reject session expire exit limit missed partial close unconfigured symbol signal exit cleanup' },
    { heading: 'User control during a trade',    anchor: 'user-control',      body: 'edit sl target snooze trail sl trigger close square off toggle auto trade order placement' },
    { heading: 'Manual execute vs Auto-Trade',   anchor: 'manual-vs-auto',    body: 'manual execute vs auto trade entry splitting doubling rule portfolio reserve single order' },
  ],
};

export default Section_autotrade;
